<template>
  <div class="public-layout min-h-screen bg-bg-primary">
    <!-- Fixed Navigation - V3 Storytelling Header -->
    <PublicHeader />

    <!-- Main Content Slot with padding for fixed nav -->
    <main
      class="main-content pt-24"
      role="main"
      :aria-label="mainContentAriaLabel"
    >
      <slot />
    </main>

    <!-- Footer - V3 Storytelling Footer -->
    <PublicFooter />
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import PublicHeader from '@/components/layout/PublicHeader.vue'
import PublicFooter from '@/components/layout/PublicFooter.vue'

/**
 * PublicLayout V3 - Storytelling Visual Design
 *
 * Updated to use V3 components:
 * - PublicHeader: Floating pill navigation
 * - PublicFooter: Dark footer with legal disclaimer
 *
 * Applies ADR-043: Vue Landing Components Architecture (Updated for V3)
 */

// Props
const props = defineProps({
  /**
   * Titulo personalizado para aria-label del main content
   */
  pageTitle: {
    type: String,
    default: 'Contenido principal'
  }
})

// Emits
const emit = defineEmits([
  'page-loaded'
])

// Computed
const mainContentAriaLabel = computed(() => `${props.pageTitle} - Olvidadme`)

// Lifecycle
onMounted(() => {
  emit('page-loaded', { layout: 'public-v3' })
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Main content styling with proper spacing for fixed nav */
.main-content {
  padding-top: 6rem; /* 96px to compensate for floating pill nav */
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .main-content {
    padding-top: 5rem; /* 80px on mobile */
  }
}

/* Ensure smooth scrolling for the entire layout */
.public-layout {
  scroll-behavior: smooth;
}

/* Focus styles for accessibility */
.main-content:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-accent-500), 0 0 0 4px white;
}

/* Print styles */
@media print {
  .public-layout {
    background: white;
  }

  .main-content {
    padding-top: 0;
  }
}
</style>
